/**
 * IndexedDB: the outbox and the read cache (brief §14a.2, §14a.3).
 *
 * Hand-rolled over the raw API rather than pulled from npm. It is two object stores and six
 * operations, the browser API is unpleasant but small, and a wrapper library would be the
 * largest thing in the front-end bundle after MapLibre.
 *
 * `navigator.storage.persist()` is requested on the **first write to the outbox** and not at
 * start-up. Without it a browser is entitled to throw the database away under storage
 * pressure, and then an evening's reports are gone; asking at start-up would prompt somebody
 * who has not yet done anything worth keeping.
 */

const DB_NAME = 'hotspotter';
const DB_VERSION = 1;
const OPS = 'ops';
const CACHE = 'cache';

export type OpKind = 'sighting' | 'passby' | 'confirm' | 'report' | 'settings';

/** Brief §14a.3, field for field. */
export type Op = {
  opId: string;
  kind: OpKind;
  payload: unknown;
  /** Device time when the user tapped. Not the time it reaches the server. */
  queuedAt: number;
  attempts: number;
  lastError?: string;
};

export type CacheEntry<T = unknown> = { key: string; value: T; fetchedAt: number };

let opened: Promise<IDBDatabase> | undefined;

function open(): Promise<IDBDatabase> {
  if (opened !== undefined) return opened;
  opened = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('no IndexedDB'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OPS)) {
        const ops = db.createObjectStore(OPS, { keyPath: 'opId' });
        // The outbox is drained strictly in `queuedAt` order (brief §14a.3), so the index is
        // not a nicety: without it every flush sorts the whole store in memory.
        ops.createIndex('queuedAt', 'queuedAt');
      }
      if (!db.objectStoreNames.contains(CACHE)) {
        db.createObjectStore(CACHE, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB refused to open'));
  });
  return opened;
}

function run<T>(
  store: string,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(store, mode);
        const request = action(transaction.objectStore(store));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
      }),
  );
}

let persistenceAsked = false;

/**
 * Ask the browser not to evict us.
 *
 * Called on the first outbox write. Easy to forget and expensive to have forgotten: without
 * it the reports somebody made in a cellar bar are a cache entry the browser may reclaim.
 */
async function requestPersistence(): Promise<void> {
  if (persistenceAsked) return;
  persistenceAsked = true;
  try {
    await navigator.storage?.persist?.();
  } catch {
    // Refused or unsupported. The outbox still works; it is merely evictable.
  }
}

export const outboxStore = {
  async add(op: Op): Promise<void> {
    await requestPersistence();
    await run(OPS, 'readwrite', (store) => store.put(op));
  },

  async put(op: Op): Promise<void> {
    await run(OPS, 'readwrite', (store) => store.put(op));
  },

  async remove(opId: string): Promise<void> {
    await run(OPS, 'readwrite', (store) => store.delete(opId));
  },

  /** Everything waiting, oldest first — the order a flush must use (brief §14a.3). */
  async all(): Promise<Op[]> {
    try {
      const rows = await run<Op[]>(OPS, 'readonly', (store) =>
        store.index('queuedAt').getAll() as IDBRequest<Op[]>,
      );
      return rows;
    } catch {
      return [];
    }
  },

  async clear(): Promise<void> {
    await run(OPS, 'readwrite', (store) => store.clear());
  },
};

export const readCache = {
  async get<T>(key: string): Promise<CacheEntry<T> | undefined> {
    try {
      const entry = await run<CacheEntry<T> | undefined>(CACHE, 'readonly', (store) =>
        store.get(key),
      );
      return entry ?? undefined;
    } catch {
      // A browser with IndexedDB switched off. Everything still works online, which is the
      // only sensible way to degrade.
      return undefined;
    }
  },

  async set<T>(key: string, value: T): Promise<void> {
    try {
      await run(CACHE, 'readwrite', (store) =>
        store.put({ key, value, fetchedAt: Date.now() } satisfies CacheEntry<T>),
      );
    } catch {
      // As above.
    }
  },

  async clear(): Promise<void> {
    try {
      await run(CACHE, 'readwrite', (store) => store.clear());
    } catch {
      // As above.
    }
  },
};

/** "Stand: vor 2 Std." — an age, shown quietly, never as a warning (brief §14a.1). */
export function describeAge(fetchedAt: number, now = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - fetchedAt) / 60_000));
  if (minutes < 2) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  return `vor ${Math.round(hours / 24)} Tagen`;
}
